import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Address, Contact } from '@/api/types';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import { Menu, type MenuAnchor, type MenuNode } from '@/features/context-menu';
import { useHandheld } from '@/hooks/useMediaQuery';
import { externalDavSentence, externalProviderName, useOptionalAccountContext } from '@/app/accounts';
import { describeDavFailure } from '@/features/dav/failure';
import { isNotAvailable } from './availability';
import { contactForAddress, withAddress, type AddressMatch } from './addressMatch';
import { ContactEditor } from '@/app/lazyUi';
import './address-actions.css';

/**
 * "Add this person to contacts", hung off one address in a message header.
 *
 * ## Why the address and not the message
 *
 * The row context menu (ADR 0019) has exactly one target — the row — and a
 * message with five recipients has five answers to "add whom?". So the verb
 * lives on the *address*: the sender's name and every recipient's name in the
 * reading header become the trigger for their own menu, and the bulk
 * "add everyone" that nobody asked for never has to exist. The menu itself is
 * ADR 0019's `Menu`, as a popover on desktop and ADR 0048's bottom sheet on
 * handheld, so the keyboard, the roving focus and the close paths are the ones
 * already tested.
 *
 * ## What it offers, and why the lookup is not optional
 *
 * A duplicate card is the failure mode of every "add sender" button ever
 * shipped, so the menu answers the duplicate question *before* it offers
 * anything (`addressMatch.ts`):
 *
 *  - the address is already on a card → **Open contact**, and the header says
 *    which card, so the user is not left wondering why the offer vanished;
 *  - a card has the same display name but not this address → **Add this
 *    address to <name>**, which opens that card in the editor with the address
 *    appended, alongside the plain "Add to contacts" for when they are two
 *    different people with one name;
 *  - neither → **Add to contacts**.
 *
 * While the lookup is in flight the menu says so and offers nothing: claiming
 * "Add to contacts" and then creating a second card for someone already in the
 * book is the exact mistake the lookup exists to prevent.
 *
 * The lookup is lazy — nothing is fetched until a menu is opened — because a
 * header with a dozen recipients would otherwise be a dozen CardDAV searches
 * for a menu nobody opened. The address books *are* fetched on mount, but
 * that is one shared, account-scoped, five-minute key the contacts screen
 * already uses, so it is at most one request per account per visit.
 *
 * Saving goes through the real `ContactEditor`, never a second inline form:
 * the editor is where address books, ETag conflicts, vCard types and the
 * read-only rules already live, and a "quick save" button beside them would be
 * a second, worse contact form that silently picks a book for you.
 */

export interface AddressContactMenuProps {
  accountId: string;
  address: Address;
  /** The text the trigger shows — the display name, usually. */
  children: ReactNode;
  className?: string;
  /**
   * Show the existing card. Absent when the caller has no router to navigate
   * with, in which case the item is absent too rather than dead (ADR 0019's
   * "absent, not disabled").
   */
  onOpenContact?: ((contactId: string) => void) | undefined;
  /**
   * Render the menu as ADR 0048's bottom sheet. Defaults to the handheld
   * breakpoint; the reading pane passes its own resolved value, because the
   * message window (ADR 0020) is a desktop popup that can be narrower than
   * 60rem and must not sprout phone chrome.
   */
  handheld?: boolean | undefined;
}

/** What the editor was opened for; `undefined` means it is closed. */
type EditorTarget =
  | { mode: 'create' }
  | { mode: 'append'; contact: Contact }
  | undefined;

function fold(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

export function AddressContactMenu({
  accountId,
  address,
  children,
  className,
  onOpenContact,
  handheld: handheldProp,
}: AddressContactMenuProps) {
  const api = useApi();
  const handheldQuery = useHandheld();
  const handheld = handheldProp ?? handheldQuery;
  /**
   * The open menu's anchor *and* the element focus goes back to, captured
   * together when it opens. The trigger is read there, in the click handler,
   * rather than during render: `Menu` wants a DOM node, and reading a ref
   * while rendering is both a lint error and a lie — on the first render
   * there is no node yet.
   */
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; trigger: HTMLButtonElement } | undefined>(
    undefined,
  );
  const [editor, setEditor] = useState<EditorTarget>(undefined);

  const email = address.email.trim();
  const name = (address.name ?? '').trim();
  const open = menu !== undefined;

  const owner = useOptionalAccountContext()?.accounts.find((account) => account.id === accountId);
  // A mailbox on another provider (ADR 0107) has no address book here: nothing
  // is asked for, and the menu says so in one line.
  const external = externalProviderName(owner);

  const booksQuery = useQuery({
    queryKey: queryKeys.account(accountId).addressBooks(),
    queryFn: ({ signal }) => api.contacts.listBooks(accountId, signal),
    staleTime: STALE_TIMES.metadata,
    retry: false,
    enabled: external === undefined,
  });
  const books = booksQuery.data ?? [];
  const writable = books.filter((book) => !book.readOnly);

  /*
   * One query for the whole question. The server's search is a substring
   * match, so a second round trip by name is only made when the address
   * itself found nothing — and the exactness of both answers is decided here,
   * in `contactForAddress`, not by whatever the search happened to return.
   */
  const lookup = useQuery<AddressMatch>({
    queryKey: queryKeys.account(accountId).contactLookup({ email: fold(email), name: fold(name) }),
    queryFn: async ({ signal }) => {
      const byAddress = await api.contacts.autocomplete(accountId, email, signal);
      const found = contactForAddress(byAddress, address);
      if (found.kind !== 'none' || name === '') return found;
      const byName = await api.contacts.autocomplete(accountId, name, signal);
      return contactForAddress(byName, address);
    },
    enabled: open && email !== '' && external === undefined,
    staleTime: STALE_TIMES.list,
    retry: false,
  });

  const match = lookup.data;
  const mailbox = owner?.email;
  const failure = useMemo(
    () => (lookup.error ? describeDavFailure('contacts', lookup.error, mailbox) : undefined),
    [lookup.error, mailbox],
  );
  // Nothing to retry: this mailbox has no address book, or its password has to
  // be entered again first. Either way the menu says so and offers nothing.
  const unavailable = isNotAvailable(lookup.error) || failure?.reauth === true;

  const items = useMemo<MenuNode[]>(() => {
    if (external !== undefined) {
      return [
        {
          kind: 'item',
          id: 'unavailable',
          label: externalDavSentence(external),
          disabled: true,
          hint: 'Nobody can be added to contacts from here.',
          onSelect: () => {},
        },
      ];
    }
    // Status lines, not actions: rendered as disabled items (`aria-disabled`,
    // so still read out, and pressing one leaves the menu open) with the
    // reason under the label, rather than as buttons that do nothing.
    if (unavailable && failure) {
      return [
        {
          kind: 'item',
          id: 'unavailable',
          label: failure.title,
          disabled: true,
          hint: failure.reauth
            ? 'Re-enter its password in Settings → Accounts, then try again.'
            : 'Nobody can be added to contacts from here.',
          onSelect: () => {},
        },
      ];
    }
    if (lookup.isPending) {
      return [
        {
          kind: 'item',
          id: 'checking',
          label: 'Checking contacts…',
          disabled: true,
          hint: 'Looking for this address in your contacts first, so no one is added twice.',
          onSelect: () => {},
        },
      ];
    }
    if (lookup.isError) {
      return [
        {
          kind: 'item',
          id: 'retry',
          label: failure ? `${failure.title} — try again` : 'Couldn’t check contacts — try again',
          onSelect: () => void lookup.refetch(),
        },
      ];
    }

    const nodes: MenuNode[] = [];
    if (match?.kind === 'contact') {
      if (onOpenContact !== undefined) {
        const contactId = match.contact.id;
        nodes.push({
          kind: 'item',
          id: 'open-contact',
          label: 'Open contact',
          onSelect: () => onOpenContact(contactId),
        });
      }
      return nodes;
    }

    // Nothing to save into is a reason to offer nothing, not a reason to
    // offer a form whose Save can only fail.
    if (writable.length === 0) return nodes;

    if (match?.kind === 'name') {
      const existing = match.contact;
      nodes.push({
        kind: 'item',
        id: 'append-address',
        label: `Add this address to ${existing.displayName}`,
        onSelect: () => setEditor({ mode: 'append', contact: existing }),
      });
    }
    nodes.push({
      kind: 'item',
      id: 'add-contact',
      label: 'Add to contacts',
      onSelect: () => setEditor({ mode: 'create' }),
    });
    return nodes;
  }, [external, failure, lookup, match, onOpenContact, unavailable, writable.length]);

  const header =
    match?.kind === 'contact'
      ? `${email} is already in ${match.contact.displayName}`
      : undefined;

  if (email === '') return <>{children}</>;

  return (
    <>
      <button
        type="button"
        className={className === undefined ? 'address-action' : `address-action ${className}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Contact actions for ${email}`}
        onClick={(event) =>
          setMenu({
            anchor: event.currentTarget.getBoundingClientRect(),
            trigger: event.currentTarget,
          })
        }
      >
        {children}
      </button>
      {menu !== undefined && (
        <Menu
          label={`Contact actions for ${email}`}
          {...(header !== undefined ? { header } : {})}
          items={items}
          anchor={menu.anchor}
          returnFocusTo={menu.trigger}
          onClose={() => setMenu(undefined)}
          variant={handheld ? 'sheet' : 'popover'}
        />
      )}
      {editor !== undefined && (
        <ContactEditor
          open
          accountId={accountId}
          books={books}
          {...(editor.mode === 'append'
            ? { contact: withAddress(editor.contact, email) }
            : { prefill: { displayName: name, email } })}
          {...(writable[0] !== undefined ? { defaultBookId: writable[0].id } : {})}
          onClose={() => setEditor(undefined)}
        />
      )}
    </>
  );
}
